/**
 * United Auth — Vanilla JS (no jQuery)
 * Handles login, register forms, password toggles, and popup logic.
 */
(function () {
    'use strict';

    const config = window.united_auth_obj || {};
    const i18n = config.i18n || {};

    // =========================================================================
    // Utility helpers
    // =========================================================================

    function qs(selector, parent) {
        return (parent || document).querySelector(selector);
    }

    function qsa(selector, parent) {
        return (parent || document).querySelectorAll(selector);
    }

    function on(el, event, handler) {
        if (el) el.addEventListener(event, handler);
    }

    // =========================================================================
    // Password toggle
    // =========================================================================

    function initPasswordToggles() {
        qsa('.password-toggle').forEach(function (btn) {
            on(btn, 'click', function () {
                var input = btn.parentElement.querySelector('input');
                if (!input) return;

                var isPassword = input.type === 'password';
                input.type = isPassword ? 'text' : 'password';

                var icon = btn.querySelector('i');
                if (icon) {
                    icon.className = isPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye';
                }

                btn.setAttribute('aria-label', isPassword ? (i18n.hide_password || 'Hide') : (i18n.show_password || 'Show'));
            });
        });
    }

    function getRandomChar(chars) {
        if (window.crypto && window.crypto.getRandomValues) {
            var random = new Uint32Array(1);
            window.crypto.getRandomValues(random);
            return chars[random[0] % chars.length];
        }

        return chars[Math.floor(Math.random() * chars.length)];
    }

    function shuffleString(value) {
        var chars = value.split('');

        for (var i = chars.length - 1; i > 0; i--) {
            var j = Math.floor(Math.random() * (i + 1));
            var temp = chars[i];
            chars[i] = chars[j];
            chars[j] = temp;
        }

        return chars.join('');
    }

    function generateStrongPassword() {
        var lower = 'abcdefghijkmnopqrstuvwxyz';
        var upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
        var digits = '23456789';
        var symbols = '!@#$%&*?';
        var all = lower + upper + digits + symbols;
        var password = [
            getRandomChar(lower),
            getRandomChar(upper),
            getRandomChar(digits),
            getRandomChar(symbols)
        ].join('');

        while (password.length < 14) {
            password += getRandomChar(all);
        }

        return shuffleString(password);
    }

    function initPasswordGenerator() {
        qsa('.password-generate').forEach(function (btn) {
            on(btn, 'click', function () {
                var form = btn.closest('form');
                if (!form) return;

                var password = qs('input[name="password"]', form);
                var confirm = qs('input[name="password_confirm"]', form);
                if (!password || !confirm) return;

                var generatedPassword = generateStrongPassword();
                password.value = generatedPassword;
                confirm.value = generatedPassword;

                password.type = 'text';
                confirm.type = 'text';

                password.dispatchEvent(new Event('input', { bubbles: true }));
                confirm.dispatchEvent(new Event('input', { bubbles: true }));
                password.focus();
            });
        });
    }

    // =========================================================================
    // Form helpers
    // =========================================================================

    function setLoading(form, loading) {
        var btn = qs('.btn-uauth-action', form);
        if (!btn) return;

        var textEl = qs('.btn-uauth-action__text', btn);
        var loaderEl = qs('.btn-uauth-action__loader', btn);

        if (loading) {
            btn.disabled = true;
            btn.classList.add('is-loading');

            if (textEl) {
                // Store original text
                if (!btn.dataset.originalText) {
                    btn.dataset.originalText = textEl.textContent;
                }

                // Change text if it's registration or login
                if (form.id === 'united-auth-register-form') {
                    textEl.textContent = i18n.registering || 'Реєстрація';
                } else if (form.id === 'united-auth-login-form') {
                    textEl.textContent = i18n.logging_in || 'Вхід';
                } else {
                    textEl.textContent = i18n.loading || 'Loading';
                }

                textEl.style.opacity = '0.7';
            }
            if (loaderEl) loaderEl.style.display = 'inline-flex';
        } else {
            btn.disabled = false;
            btn.classList.remove('is-loading');
            if (textEl) {
                if (btn.dataset.originalText) {
                    textEl.textContent = btn.dataset.originalText;
                    delete btn.dataset.originalText;
                }
                textEl.style.opacity = '';
                textEl.style.visibility = '';
            }
            if (loaderEl) loaderEl.style.display = 'none';
        }
    }

    function showFeedback(form, message, type) {
        var feedback = qs('.form-feedback', form);
        if (!feedback) return;

        feedback.className = 'form-feedback form-feedback--' + type + ' is-visible mt-3';
        feedback.textContent = message;

        // Auto-hide success after 5s
        if (type === 'success') {
            setTimeout(function () {
                feedback.classList.remove('is-visible');
            }, 5000);
        }
    }

    function clearFeedback(form) {
        var feedback = qs('.form-feedback', form);
        if (feedback) {
            feedback.textContent = '';
            feedback.className = 'form-feedback';
        }
    }

    function resetRecaptcha() {
        if (window.grecaptcha) {
            try { grecaptcha.reset(); } catch (e) { /* ignore */ }
        }
    }

    function getRecaptchaResponse(form) {
        var input = qs('[name="g-recaptcha-response"]', form);
        if (input && input.value) {
            return input.value;
        }

        var responses = qsa('[name="g-recaptcha-response"]');
        for (var i = 0; i < responses.length; i++) {
            if (responses[i].value) {
                return responses[i].value;
            }
        }

        if (window.grecaptcha) {
            try { return grecaptcha.getResponse(); } catch (e) { /* ignore */ }
        }

        return '';
    }

    // =========================================================================
    // AJAX via fetch
    // =========================================================================

    function ajaxPost(data) {
        var body = new URLSearchParams();
        for (var key in data) {
            if (data.hasOwnProperty(key)) {
                body.append(key, data[key]);
            }
        }

        return fetch(config.ajax_url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString()
        }).then(function (res) {
            return res.json();
        });
    }

    // =========================================================================
    // Login form
    // =========================================================================

    function initLoginForm() {
        var form = qs('#united-auth-login-form');
        if (!form) return;

        on(form, 'submit', function (e) {
            e.preventDefault();
            clearFeedback(form);
            setLoading(form, true);

            var username = qs('input[name="username"]', form);
            var password = qs('input[name="password"]', form);
            var remember = qs('input[name="remember"]', form);
            var redirectTo = qs('input[name="redirect_to"]', form);

            var recaptchaResponse = getRecaptchaResponse(form);

            var payload = {
                action: 'united_auth_login',
                security: config.nonce,
                username: username ? username.value.trim() : '',
                password: password ? password.value : '',
                remember: (remember && remember.checked) ? 'true' : 'false'
            };

            var redirectVal = (redirectTo && redirectTo.value) ? redirectTo.value : (new URLSearchParams(window.location.search).get('redirect_to') || '');
            if (redirectVal) {
                payload['redirect_to'] = redirectVal;
            }

            if (recaptchaResponse) {
                payload['g-recaptcha-response'] = recaptchaResponse;
            }

            ajaxPost(payload)
                .then(function (response) {
                    if (response.success) {
                        window.location.href = response.data.redirect_url || config.redirect_url || '/';
                    } else {
                        showFeedback(form, response.data.message, 'error');
                        setLoading(form, false);
                        resetRecaptcha();
                    }
                })
                .catch(function () {
                    showFeedback(form, i18n.error_generic || 'Something went wrong.', 'error');
                    setLoading(form, false);
                    resetRecaptcha();
                });
        });
    }

    // =========================================================================
    // Register form
    // =========================================================================

    function initRegisterForm() {
        var form = qs('#united-auth-register-form');
        if (!form) return;

        var steps = qsa('.auth-form-step', form);
        var indicators = qsa('.auth-form-step-indicator', form);
        var currentStep = 1;

        function updateSteps() {
            steps.forEach(function (step) {
                step.classList.toggle('active', parseInt(step.dataset.step) === currentStep);
            });

            indicators.forEach(function (ind) {
                var stepNum = parseInt(ind.dataset.step);
                ind.classList.toggle('active', stepNum === currentStep);
                ind.classList.toggle('completed', stepNum < currentStep);

                if (stepNum === currentStep) {
                    var label = qs('.step-label', ind).textContent;
                    var mobileNum = qs('.current-step-num', form);
                    var mobileLabel = qs('.current-step-label', form);
                    if (mobileNum) mobileNum.textContent = currentStep;
                    if (mobileLabel) mobileLabel.textContent = label;
                }
            });

            // Scroll to top of form on mobile
            if (window.innerWidth < 768) {
                form.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }

        function validateEmail(email) {
            var re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(email);
        }

        function showFieldError(input, msg) {
            input.classList.add('error');
            var group = input.closest('.form-group');
            if (group) {
                var errDiv = qs('.field-error', group);
                if (!errDiv) {
                    errDiv = document.createElement('div');
                    errDiv.className = 'field-error';
                    group.appendChild(errDiv);
                }
                errDiv.textContent = msg;
                errDiv.style.display = 'block';
            }
        }

        function clearFieldError(input) {
            input.classList.remove('error');
            var group = input.closest('.form-group');
            if (group) {
                var errDiv = qs('.field-error', group);
                if (errDiv) {
                    errDiv.style.display = 'none';
                }
            }
        }

        function validateStep(stepNum) {
            var stepEl = qs('.auth-form-step[data-step="' + stepNum + '"]', form);
            if (!stepEl) return true;

            var inputs = qsa('input[required]', stepEl);
            var isValid = true;
            var firstErrorInput = null;

            inputs.forEach(function (input) {
                var val = input.value.trim();
                var msg = '';

                if (!val) {
                    msg = i18n.field_required || 'Field required';
                } else if (input.name === 'email' && !validateEmail(val)) {
                    msg = i18n.email_invalid || 'Invalid email';
                } else if (input.name === 'username' && val.length < 3) {
                    msg = i18n.username_too_short || 'Too short';
                } else if (input.name === 'password' && val.length < 8) {
                    msg = i18n.pass_min_length || 'Min 8 chars';
                } else if (input.name === 'password_confirm') {
                    var pass = qs('input[name="password"]', form).value;
                    if (val !== pass) {
                        msg = i18n.password_mismatch || 'Mismatch';
                    }
                }

                if (msg) {
                    isValid = false;
                    showFieldError(input, msg);
                    if (!firstErrorInput) firstErrorInput = input;
                } else {
                    clearFieldError(input);
                }

                // Clear on input
                on(input, 'input', function () {
                    clearFieldError(input);
                }, { once: true });
            });

            if (firstErrorInput) {
                firstErrorInput.focus();
            }

            return isValid;
        }

        // Next Buttons
        qsa('.btn-next-step', form).forEach(function (btn) {
            on(btn, 'click', function () {
                if (validateStep(currentStep)) {
                    currentStep++;
                    updateSteps();
                }
            });
        });

        // Prev Buttons
        qsa('.btn-prev-step', form).forEach(function (btn) {
            on(btn, 'click', function () {
                currentStep--;
                updateSteps();
            });
        });

        // Phone mask: +38 (0XX) XXX-XX-XX
        var phoneInput = qs('input[name="contact_info"]', form);
        if (phoneInput) {
            on(phoneInput, 'input', function (e) {
                var x = e.target.value.replace(/\D/g, '').match(/(\d{0,2})(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})/);
                if (!x) return;

                // Keep +38 prefix if possible
                var val = '';
                if (x[1]) val += '+' + x[1];
                if (x[2]) val += ' (' + x[2];
                if (x[3]) val += ') ' + x[3];
                if (x[4]) val += '-' + x[4];
                if (x[5]) val += '-' + x[5];

                e.target.value = val;
            });
        }

        on(form, 'submit', function (e) {
            e.preventDefault();

            // Final validation for the last step
            if (!validateStep(currentStep)) return;

            clearFeedback(form);

            var password = qs('input[name="password"]', form);
            var confirm = qs('input[name="password_confirm"]', form);

            // Client-side password match validation
            if (password && confirm && password.value !== confirm.value) {
                showFeedback(form, i18n.password_mismatch || 'Passwords do not match.', 'error');
                return;
            }

            setLoading(form, true);

            var recaptchaResponse = getRecaptchaResponse(form);

            var payload = {
                action: 'united_auth_register',
                security: config.nonce,
                first_name: (qs('input[name="first_name"]', form) || {}).value || '',
                last_name: (qs('input[name="last_name"]', form) || {}).value || '',
                username: (qs('input[name="username"]', form) || {}).value || '',
                email: (qs('input[name="email"]', form) || {}).value || '',
                contact_info: (qs('input[name="contact_info"]', form) || {}).value || '',
                password: password ? password.value : ''
            };

            if (recaptchaResponse) {
                payload['g-recaptcha-response'] = recaptchaResponse;
            }

            ajaxPost(payload)
                .then(function (response) {
                    if (response.success) {
                        if (response.data.redirect_url) {
                            window.location.href = response.data.redirect_url;
                        } else {
                            // Show Step 4 (Success)
                            currentStep = 4;
                            updateSteps();
                            form.reset();
                        }
                    } else {
                        showFeedback(form, response.data.message, 'error');
                    }
                    setLoading(form, false);
                    resetRecaptcha();
                })
                .catch(function (error) {
                    console.error('United Auth Register Error:', error);
                    showFeedback(form, i18n.error_generic || 'Something went wrong.', 'error');
                    setLoading(form, false);
                    resetRecaptcha();
                });
        });
    }

    // =========================================================================
    // Popup logic (if popups exist on the page)
    // =========================================================================

    function initPopups() {
        var loginPopup = qs('#login-popup');
        var registerPopup = qs('#register-popup');
        var body = document.body;

        function openPopup(popup) {
            if (!popup) return;
            popup.classList.add('active');
            popup.setAttribute('open', '');
            body.style.overflow = 'hidden';
        }

        function closePopup(popup) {
            if (!popup) return;
            popup.classList.remove('active');
            popup.removeAttribute('open');
            body.style.overflow = '';
        }

        // Open via anchor links
        qsa('a[href="#login"]').forEach(function (link) {
            on(link, 'click', function (e) {
                e.preventDefault();
                closePopup(registerPopup);
                openPopup(loginPopup);
            });
        });

        qsa('a[href="#register"]').forEach(function (link) {
            on(link, 'click', function (e) {
                e.preventDefault();
                closePopup(loginPopup);
                openPopup(registerPopup);
            });
        });

        // Close buttons
        qsa('.popup__close').forEach(function (btn) {
            on(btn, 'click', function () {
                var popup = btn.closest('.popup');
                if (popup) closePopup(popup);
            });
        });

        // Click outside
        qsa('.popup').forEach(function (popup) {
            on(popup, 'click', function (e) {
                if (e.target === popup) closePopup(popup);
            });
        });

        // Switch buttons
        qsa('.switch-to-register').forEach(function (btn) {
            on(btn, 'click', function () {
                closePopup(loginPopup);
                openPopup(registerPopup);
            });
        });

        qsa('.switch-to-login').forEach(function (btn) {
            on(btn, 'click', function () {
                closePopup(registerPopup);
                openPopup(loginPopup);
            });
        });
    }

    // =========================================================================
    // Password Reset form (two-step)
    // =========================================================================

    function initPasswordResetForm() {
        var stepRequest = qs('#ua-reset-step-request');
        var stepVerify = qs('#ua-reset-step-verify');
        if (!stepRequest) return;

        var requestForm = qs('#united-auth-reset-request-form');
        var verifyForm = qs('#united-auth-reset-verify-form');
        var loginHidden = qs('#ua-reset-login-hidden');
        var backLink = qs('#ua-reset-back');

        // Step 1: Request code
        on(requestForm, 'submit', function (e) {
            e.preventDefault();
            clearFeedback(requestForm);
            setLoading(requestForm, true);

            var loginInput = qs('input[name="login"]', requestForm);
            var contactInput = qs('input[name="contact_info"]', requestForm);
            var loginVal = loginInput ? loginInput.value.trim() : '';
            var contactVal = contactInput ? contactInput.value.trim() : '';

            ajaxPost({
                action: 'united_auth_request_reset',
                security: config.nonce,
                login: loginVal,
                contact_info: contactVal
            })
                .then(function (response) {
                    setLoading(requestForm, false);
                    if (response.success) {
                        // Show step 2
                        if (loginHidden) loginHidden.value = loginVal;
                        stepRequest.style.display = 'none';
                        stepVerify.hidden = false;
                        showFeedback(verifyForm, response.data.message, 'success');
                    } else {
                        showFeedback(requestForm, response.data.message, 'error');
                    }
                })
                .catch(function () {
                    setLoading(requestForm, false);
                    showFeedback(requestForm, i18n.error_generic || 'Something went wrong.', 'error');
                });
        });

        // Step 2: Verify code + new password
        on(verifyForm, 'submit', function (e) {
            e.preventDefault();
            clearFeedback(verifyForm);

            var newPass = qs('input[name="new_password"]', verifyForm);
            var confirmPass = qs('input[name="new_password_confirm"]', verifyForm);

            if (newPass && confirmPass && newPass.value !== confirmPass.value) {
                showFeedback(verifyForm, i18n.password_mismatch || 'Passwords do not match.', 'error');
                return;
            }

            setLoading(verifyForm, true);

            ajaxPost({
                action: 'united_auth_verify_reset',
                security: config.nonce,
                login: loginHidden ? loginHidden.value : '',
                code: (qs('input[name="code"]', verifyForm) || {}).value || '',
                new_password: newPass ? newPass.value : ''
            })
                .then(function (response) {
                    setLoading(verifyForm, false);
                    if (response.success) {
                        showFeedback(verifyForm, response.data.message, 'success');
                        verifyForm.reset();
                        // Redirect after 2s
                        if (response.data.redirect_url) {
                            setTimeout(function () {
                                window.location.href = response.data.redirect_url;
                            }, 2000);
                        }
                    } else {
                        showFeedback(verifyForm, response.data.message, 'error');
                    }
                })
                .catch(function () {
                    setLoading(verifyForm, false);
                    showFeedback(verifyForm, i18n.error_generic || 'Something went wrong.', 'error');
                });
        });

        // Back link
        on(backLink, 'click', function (e) {
            e.preventDefault();
            stepVerify.hidden = true;
            stepRequest.style.display = '';
            clearFeedback(verifyForm);
            clearFeedback(requestForm);
        });
    }

    // =========================================================================
    // Init on DOMContentLoaded
    // =========================================================================


    // =========================================================================
    // Google Identity Services (GIS) Sign-In & Sign-Up
    // =========================================================================

    function initGoogleAuth() {
        if (!config.google_enabled || !config.google_client_id) return;

        var loginBtnContainer = qs('#ua-google-login-btn');
        var registerBtnContainer = qs('#ua-google-register-btn');

        if (!loginBtnContainer && !registerBtnContainer) return;

        function handleCredentialResponse(response) {
            if (!response || !response.credential) return;

            var activeForm = qs('#united-auth-login-form') || qs('#united-auth-register-form');
            var registerFeedback = qs('#ua-google-register-feedback');

            if (activeForm) {
                clearFeedback(activeForm);
                setLoading(activeForm, true);
            }
            if (registerFeedback) {
                registerFeedback.textContent = '';
                registerFeedback.className = 'form-feedback';
            }

            var payload = {
                action: 'united_auth_google_auth',
                security: config.nonce,
                id_token: response.credential
            };

            var redirectToInput = activeForm ? qs('input[name="redirect_to"]', activeForm) : null;
            var redirectVal = (redirectToInput && redirectToInput.value) ? redirectToInput.value : (new URLSearchParams(window.location.search).get('redirect_to') || '');
            if (redirectVal) {
                payload['redirect_to'] = redirectVal;
            }

            ajaxPost(payload)
                .then(function (res) {
                    if (res.success) {
                        if (res.data && res.data.is_registration) {
                            var regForm = qs('#united-auth-register-form');
                            if (regForm) {
                                var steps = qsa('.auth-form-step', regForm);
                                var indicators = qsa('.auth-form-step-indicator', regForm);
                                steps.forEach(function (s) { s.classList.remove('active'); });
                                indicators.forEach(function (ind) { ind.classList.remove('active'); });

                                var successStep = qs('.auth-form-step[data-step="4"]', regForm);
                                var successIndicator = qs('.auth-form-step-indicator[data-step="4"]', regForm);
                                if (successStep) successStep.classList.add('active');
                                if (successIndicator) successIndicator.classList.add('active');

                                var successDesc = qs('.auth-form-success p', regForm);
                                if (successDesc && res.data.message) {
                                    successDesc.textContent = res.data.message;
                                }

                                if (res.data.redirect_url) {
                                    setTimeout(function () {
                                        window.location.href = res.data.redirect_url;
                                    }, 2000);
                                }
                            } else if (activeForm) {
                                showFeedback(activeForm, res.data.message || 'Registration successful!', 'success');
                                if (res.data.redirect_url) {
                                    setTimeout(function () {
                                        window.location.href = res.data.redirect_url;
                                    }, 2000);
                                }
                            }
                        } else {
                            window.location.href = res.data.redirect_url || config.redirect_url || '/';
                        }
                    } else {
                        var errorMsg = (res.data && res.data.message) ? res.data.message : (i18n.error_generic || 'Error');
                        if (activeForm) {
                            showFeedback(activeForm, errorMsg, 'error');
                            setLoading(activeForm, false);
                        }
                        if (registerFeedback) {
                            registerFeedback.className = 'form-feedback form-feedback--error is-visible mt-3';
                            registerFeedback.textContent = errorMsg;
                        }
                    }
                })
                .catch(function () {
                    var genericMsg = i18n.error_generic || 'Something went wrong.';
                    if (activeForm) {
                        showFeedback(activeForm, genericMsg, 'error');
                        setLoading(activeForm, false);
                    }
                    if (registerFeedback) {
                        registerFeedback.className = 'form-feedback form-feedback--error is-visible mt-3';
                        registerFeedback.textContent = genericMsg;
                    }
                });
        }

        function renderGoogleButtons() {
            if (!window.google || !window.google.accounts || !window.google.accounts.id) return;

            try {
                window.google.accounts.id.initialize({
                    client_id: config.google_client_id,
                    callback: handleCredentialResponse,
                    auto_select: false
                });

                if (loginBtnContainer) {
                    window.google.accounts.id.renderButton(loginBtnContainer, {
                        theme: 'outline',
                        size: 'large',
                        type: 'standard',
                        text: 'signin_with',
                        shape: 'rectangular',
                        logo_alignment: 'left',
                        width: loginBtnContainer.offsetWidth || 300
                    });
                }

                if (registerBtnContainer) {
                    window.google.accounts.id.renderButton(registerBtnContainer, {
                        theme: 'outline',
                        size: 'large',
                        type: 'standard',
                        text: 'signup_with',
                        shape: 'rectangular',
                        logo_alignment: 'left',
                        width: registerBtnContainer.offsetWidth || 300
                    });
                }
            } catch (err) {
                console.error('Google Sign-In initialization failed:', err);
            }
        }

        if (window.google && window.google.accounts && window.google.accounts.id) {
            renderGoogleButtons();
        } else {
            var retries = 0;
            var interval = setInterval(function () {
                retries++;
                if (window.google && window.google.accounts && window.google.accounts.id) {
                    clearInterval(interval);
                    renderGoogleButtons();
                } else if (retries > 50) {
                    clearInterval(interval);
                }
            }, 100);
        }
    }

    function init() {
        initGoogleAuth();
        initPasswordToggles();
        initPasswordGenerator();
        initLoginForm();
        initRegisterForm();
        initPasswordResetForm();
        initPopups();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
